import { useState } from "react";
import { Sparkles, Bot } from "lucide-react";
import { useVersions, useSaveVersion, useVersionCompare, useRestoreVersion } from "../../../lib/presentationQueries";
import PromptDialog from "./PromptDialog";
import ConfirmDialog from "./ConfirmDialog";

function DiffSummary({ compare }) {
  if (!compare) return null;
  const rows = [
    ["Idea cards", compare.idea_cards], ["Sections", compare.sections], ["Beats", compare.beats],
    ["Annotations", compare.annotations], ["File links", compare.asset_links],
  ];
  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-4 mb-4" data-testid="version-compare-summary">
      <p className="text-sm font-medium text-ink mb-2">What changed between the two selected versions</p>
      <div className="grid sm:grid-cols-2 gap-2 text-sm text-ink-soft">
        {rows.map(([label, diff]) => (
          <div key={label} data-testid={`version-compare-${label.toLowerCase().replace(/\s/g, "-")}`}>
            {label}: <span className="text-ink">{diff.added.length} added, {diff.removed.length} removed, {diff.changed.length} changed</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function VersionHistoryArea({ workspaceId, presId }) {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [compareFrom, setCompareFrom] = useState(null);
  const [compareTo, setCompareTo] = useState(null);
  const [pendingRestoreId, setPendingRestoreId] = useState(null);
  const { data } = useVersions(workspaceId, presId);
  const saveVersion = useSaveVersion(workspaceId, presId);
  const restoreVersion = useRestoreVersion(workspaceId, presId);
  const { data: compareData } = useVersionCompare(workspaceId, presId, compareFrom, compareTo);
  const versions = data?.items || [];

  function toggleCompare(versionId) {
    if (compareFrom === versionId) { setCompareFrom(null); return; }
    if (compareTo === versionId) { setCompareTo(null); return; }
    if (!compareFrom) { setCompareFrom(versionId); return; }
    if (!compareTo) { setCompareTo(versionId); return; }
    setCompareFrom(versionId);
    setCompareTo(null);
  }

  return (
    <div data-testid="presentation-version-history-area">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-xl text-ink">Version History</h2>
        <button
          onClick={() => setSaveDialogOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-terracotta hover:text-terracotta/80"
          data-testid="version-save-as"
        >
          Save as version
        </button>
      </div>
      <p className="text-xs text-ink-soft mb-4">
        Pick two versions below to compare, or restore any version — InterAct always checkpoints the current
        state first, and no version is ever deleted.
      </p>
      <DiffSummary compare={compareData} />
      <div className="space-y-2" data-testid="version-history-list">
        {versions.map((v) => {
          const selected = compareFrom === v.version_id || compareTo === v.version_id;
          return (
            <div
              key={v.version_id}
              className={`flex items-center justify-between gap-3 border rounded-lg px-4 py-3 ${selected ? "border-terracotta bg-terracotta/5" : "border-ink/15 bg-white/50"}`}
              data-testid={`version-${v.version_id}`}
            >
              <button onClick={() => toggleCompare(v.version_id)} className="flex items-center gap-3 min-w-0 text-left" data-testid={`version-${v.version_id}-select`}>
                {v.kind === "manual" ? <Sparkles size={15} className="text-terracotta shrink-0" /> : <Bot size={15} className="text-ink-soft shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm text-ink truncate">{v.label}</p>
                  <p className="text-xs text-ink-soft">#{v.sequence_number} · {v.kind}</p>
                </div>
              </button>
              <button onClick={() => setPendingRestoreId(v.version_id)} className="text-xs text-ink-soft hover:text-terracotta shrink-0" data-testid={`version-${v.version_id}-restore`}>
                Restore
              </button>
            </div>
          );
        })}
        {versions.length === 0 && <p className="text-ink-soft text-sm" data-testid="version-history-empty">No versions saved yet.</p>}
      </div>
      <PromptDialog
        open={saveDialogOpen} onOpenChange={setSaveDialogOpen} title="Save as version" placeholder="e.g. First full draft"
        confirmLabel="Save" onConfirm={(label) => saveVersion.mutate(label)} testId="version-save-dialog"
      />
      <ConfirmDialog
        open={!!pendingRestoreId} onOpenChange={(o) => !o && setPendingRestoreId(null)}
        title="Restore this version?" confirmLabel="Restore"
        description="InterAct saves the current state as an automatic checkpoint first, so you can always come back — no version is ever deleted."
        onConfirm={() => { restoreVersion.mutate(pendingRestoreId); setPendingRestoreId(null); }}
        testId="version-restore-confirm"
      />
    </div>
  );
}
